import { Suspense, useRef } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import { useFormContext } from 'react-hook-form'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import calculateIngredientsCost from '@ancon/wildcat-utils/product/calculateIngredientsCost'
import calculateAddonGroupsCost from '@ancon/wildcat-utils/product/calculateAddonGroupCost'
import ProductPlaceholderIcon from '@ancon/wildcat-ui/shared/static/placeholders/product-v2.svg'

import BodyText from '../../app/components/BodyText'
import HeadingText from '../../app/components/HeadingText'
import AdvancedPricingTag from '../components/AdvancedPricingTag'
import ProductAllergyInformation from '../components/ProductAllergyInformation'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  productDetailsVariantByIdSelector,
  productsDetailsAddonGroupsSelector,
  productsDetailsAllowDynamicPricingSelector,
  productsDetailsDescriptionSelector,
  productsDetailsHasVariantsSelector,
  productsDetailsImageIdSelector,
  productsDetailsIngredientsSelector,
  productsDetailsMaximumIngredientsForDynamicAddonSelector,
  productsDetailsProductIdSelector,
  productsDetailsProductNameSelector,
  productsDetailsVariantsSelector,
} from '../store/productSelectors'
import ProductName from '../components/ProductName'
import { ConfigureProductFormState } from '../types'
import { outletSelectedOutletListItemTenantIdSelector } from '../../outlet/store/outletSelector'
import getProductImageUrl from '../utils/getProductImageUrl'
import getProductDetailsCurrency from '../utils/getProductDetailsCurrency'

import styles from './ProductMetaForm.module.scss'

type ProductMetaFormProps = {
  showFloatingHeader?: boolean
}

export default function ProductMetaForm({
  showFloatingHeader,
}: ProductMetaFormProps) {
  const { watch } = useFormContext<ConfigureProductFormState>()
  const [selectedVariantId, selectedIngredients, selectedAddons] = watch([
    'selectedVariantId',
    'selectedIngredients',
    'selectedAddons',
  ])
  const imageCacheBuster = useRef<string>(Date.now().toString())

  const tenantId = useAppSelector(outletSelectedOutletListItemTenantIdSelector)
  const imageId = useAppSelector(productsDetailsImageIdSelector)
  const hasVariants = useAppSelector(productsDetailsHasVariantsSelector)
  const variants = useAppSelector(productsDetailsVariantsSelector)
  const productId = useAppSelector(productsDetailsProductIdSelector)
  const productName = useAppSelector(productsDetailsProductNameSelector)
  const description = useAppSelector(productsDetailsDescriptionSelector)
  const selectedVariant = useAppSelector(state =>
    productDetailsVariantByIdSelector(state, selectedVariantId),
  )
  const addonGroups = useAppSelector(productsDetailsAddonGroupsSelector)
  const ingredients = useAppSelector(productsDetailsIngredientsSelector)
  const allowDynamicPricing = useAppSelector(
    productsDetailsAllowDynamicPricingSelector,
  )
  const maximumDynamicPricingIngredients = useAppSelector(
    productsDetailsMaximumIngredientsForDynamicAddonSelector,
  )
  // The maximum only bounds the dynamic pricing deductions, so it must not
  // gate the whole calculation. Replacement prices are always charged by the
  // backend, also for products without dynamic pricing.
  const ingredientsPrice = ingredients
    ? calculateIngredientsCost(
        ingredients,
        selectedIngredients,
        allowDynamicPricing,
        maximumDynamicPricingIngredients ?? undefined,
      )
    : 0
  const addonGroupsPrice = addonGroups
    ? calculateAddonGroupsCost(addonGroups, selectedAddons)
    : 0

  const priceAddition = ingredientsPrice + addonGroupsPrice

  const selectedVariantPrice = selectedVariant?.variantPrice

  function getVariantName() {
    if (hasVariants && !!selectedVariant) {
      return selectedVariant.name
    }

    return productName
  }

  function getFormattedPriceAddition() {
    const currency = getProductDetailsCurrency(variants, selectedVariant)

    return getFormattedCurrency(priceAddition, currency)
  }

  function getProductImageSrc() {
    if (imageId && tenantId && productId) {
      return getProductImageUrl(
        tenantId,
        imageId,
        productId,
        imageCacheBuster.current,
      )
    }

    return null
  }

  const imageSrc = getProductImageSrc()

  return (
    <div className={styles.container}>
      <div
        className={clsx(styles.floatingHeader, {
          [styles.show]: showFloatingHeader,
        })}
      >
        <HeadingText as="h2">{getVariantName()}</HeadingText>
      </div>

      <div className={styles.imageContainer}>
        {imageSrc && <img src={imageSrc} alt="product" />}
        {!imageId && (
          <Suspense>
            <Image
              alt={productName || 'product placeholder image'}
              className={styles.productPlaceholderImage}
              src={ProductPlaceholderIcon}
              width={60}
              height={60}
            />
          </Suspense>
        )}
      </div>

      <div className={styles.metaContainer}>
        <ProductName as="h2">{getVariantName()}</ProductName>
        <div className={styles.pricingContainer}>
          {!!selectedVariantPrice && (
            <HeadingText as="h3">
              {getFormattedCurrency(
                selectedVariantPrice.unitPrice,
                selectedVariantPrice.unitPriceCurrency,
              )}
            </HeadingText>
          )}
          {priceAddition > 0 && (
            <BodyText
              color="body-1"
              className={clsx(styles.priceAddition, {
                [styles.hasSelectedVariant]: !!selectedVariantPrice,
              })}
            >
              <BodyText color="body-1" as="span">
                +
              </BodyText>
              {getFormattedPriceAddition()}
            </BodyText>
          )}
        </div>

        {!!selectedVariantPrice?.showPricePresetLabel &&
          !!selectedVariantPrice?.pricePresetName && (
            <AdvancedPricingTag
              presetName={selectedVariantPrice.pricePresetName}
            />
          )}

        {description && <BodyText color="body-2">{description}</BodyText>}
        <ProductAllergyInformation />
      </div>
    </div>
  )
}
